import React, { ReactElement, useEffect, useRef, useState } from "react";
import WifiIcon from "@mui/icons-material/Wifi";
import CloseIcon from "@mui/icons-material/Close";
import { offlineStrings } from "./offlineStrings";
import useIsOffline from "../../../hooks/useIsOffline";

const strings = offlineStrings;

export const backOnlineNoticeDurationMs = 10 * 1000;

/**
 * Offline design D: a note in the top corner when the connection comes back
 * after the offline banner showed. It closes by itself after 10 seconds.
 */
const BackOnlineNotice = (): ReactElement | null => {
  const isOffline = useIsOffline();
  const wasOffline = useRef(isOffline);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (isOffline) {
      wasOffline.current = true;
      setIsOpen(false);
      return;
    }

    if (!wasOffline.current) {
      return;
    }

    wasOffline.current = false;
    setIsOpen(true);
    const timer = window.setTimeout(
      () => setIsOpen(false),
      backOnlineNoticeDurationMs
    );

    return () => window.clearTimeout(timer);
  }, [isOffline]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      role="status"
      className="fixed left-4 right-4 top-[72px] z-50 flex items-start gap-3 rounded-lg border border-[#10B981] bg-white px-4 py-[14px] text-black shadow-[0_8px_24px_rgba(0,0,0,0.12)] md:left-auto md:right-10 md:top-6 md:w-[380px]"
    >
      <div
        aria-hidden="true"
        className="flex h-7 w-7 flex-none items-center justify-center rounded-md bg-[#DFF6EC]"
      >
        <WifiIcon sx={{ fontSize: 18, color: "#047857" }} />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5 text-[14px] leading-[1.45]">
        <span className="font-semibold">{strings.backOnlineTitle}</span>
        <span>{strings.backOnlineText}</span>
      </div>
      <button
        type="button"
        aria-label="Close"
        onClick={() => setIsOpen(false)}
        className="flex h-7 w-7 flex-none items-center justify-center rounded-md text-[#333333] hover:bg-[#F7F7F8] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cpaGold"
      >
        <CloseIcon sx={{ fontSize: 16 }} />
      </button>
    </div>
  );
};

export default BackOnlineNotice;
